// Localização: frontend/src/components/Navigation/MobileHeader.js (Refatorado - Fundo Branco)

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../context/PermissionContext';
import { getMyProfile } from '../../api/api';
import { getNotificacoes } from '../../api/notificacaoApi';
import { usePlanAccess } from '../../hooks/usePlanAccess';
import { 
    Bell, 
    User, 
    ChevronDown, 
    Shield, 
    LogOut, 
    CreditCard, 
    AlertCircle, 
    Key, 
    Sparkles
} from 'lucide-react';
import { getUsuarioFotoUrl } from '../../utils/imageUtils';
import { AIProvider } from '../../context/AIContext';
import ChatInterfaceMini from '../AIAssistant/ChatInterfaceMini';


// --------------------------------------------------------------------------------
// 1. COMPONENTE MOBILE HEADER
// --------------------------------------------------------------------------------

const MobileHeader = () => {
    const { user, handleLogout, setUser, subscription } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { isModuleAvailable, currentPlanoId } = usePlanAccess();
    const { checkPermission } = usePermissions();
    
    // Estados para dropdowns
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isNotificacoesOpen, setIsNotificacoesOpen] = useState(false);
    // Estado para dropdown de IA - persiste entre navegações usando localStorage
    const [isIADropdownOpen, setIsIADropdownOpen] = useState(() => {
        // Carregar estado do localStorage ao montar
        const saved = localStorage.getItem('ai_dropdown_open');
        return saved === 'true';
    });
    const prevLocationRef = useRef(location.pathname);
    
    // Persistir estado no localStorage quando mudar
    useEffect(() => {
        localStorage.setItem('ai_dropdown_open', String(isIADropdownOpen));
    }, [isIADropdownOpen]);
    
    // Preservar dropdown durante navegação
    useEffect(() => {
        // Se a rota mudou mas o dropdown estava aberto, mantê-lo aberto
        if (prevLocationRef.current !== location.pathname) {
            prevLocationRef.current = location.pathname;
            // Pequeno delay para garantir que a navegação foi concluída
            const timer = setTimeout(() => {
                const saved = localStorage.getItem('ai_dropdown_open');
                if (saved === 'true' && !isIADropdownOpen) {
                    setIsIADropdownOpen(true);
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [location.pathname]);
    const [userFotoUrl, setUserFotoUrl] = useState(null);
    const [notificacoes, setNotificacoes] = useState([]);
    const [loadingNotificacoes, setLoadingNotificacoes] = useState(false);
    
    // Refs para fechar dropdowns ao clicar fora
    const menuRef = useRef(null);
    const notificacoesRef = useRef(null);
    const iaDropdownRef = useRef(null);
    
    // Verificar se usuário tem plano Premium (ID: 3) e acesso ao módulo financeiro
    const isPremium = currentPlanoId === 3 && isModuleAvailable('financeiro');

    // Verificar se usuário tem acesso ao módulo de IA (permissão RBAC + disponibilidade no plano)
    const hasIAAccess = checkPermission('ia', 'can_access') && isModuleAvailable('ia');

    // Buscar fotoURL do perfil se não estiver no contexto
    useEffect(() => {
        // Se já tem fotoURL no user e não é string vazia, usar ela
        if (user?.fotoURL && user.fotoURL.trim() !== '') {
            setUserFotoUrl(user.fotoURL);
            return;
        }

        // Se não tem fotoURL válida e ainda não buscou, buscar do perfil
        const fetchUserPhoto = async () => {
            try {
                const response = await getMyProfile();
                const profileData = response.data;
                if (profileData?.fotoURL && profileData.fotoURL.trim() !== '') {
                    setUserFotoUrl(profileData.fotoURL);
                    if (setUser) {
                        setUser(prevUser => ({
                            ...prevUser,
                            fotoURL: profileData.fotoURL
                        }));
                    }
                }
            } catch (err) {
                console.error('[MobileHeader] Erro ao buscar foto do perfil:', err);
            }
        };

        if (user && (!user.fotoURL || user.fotoURL.trim() === '')) {
            fetchUserPhoto();
        }
    }, [user?.id, setUser]);

    // Atualizar fotoUrl local quando o contexto for atualizado
    useEffect(() => {
        if (user?.fotoURL && user.fotoURL.trim() !== '') {
            setUserFotoUrl(user.fotoURL);
        } else if (user?.fotoURL === '' || user?.fotoURL === null) {
            // Limpar fotoUrl se foi removida
            setUserFotoUrl(null);
        }
    }, [user?.fotoURL]);

    // Buscar notificações (agendamentos, assinaturas e outras)
    useEffect(() => {
        const fetchNotificacoes = async () => {
            try {
                setLoadingNotificacoes(true);
                const response = await getNotificacoes();
                if (response.success) {
                    setNotificacoes(response.notificacoes || []);
                }
            } catch (error) {
                console.error('[MobileHeader] Erro ao buscar notificações:', error);
            } finally {
                setLoadingNotificacoes(false);
            }
        };

        if (user) {
            fetchNotificacoes();
            const interval = setInterval(fetchNotificacoes, 5 * 60 * 1000);
            return () => clearInterval(interval);
        }
    }, [user]);

    // Fechar menus ao clicar fora (EXCETO dropdown de IA - ele só fecha pelo botão)
    useEffect(() => {
        const handleClickOutside = (event) => {
            // NÃO fechar dropdown de IA automaticamente - ele só fecha quando o usuário clicar no botão
            // O dropdown de IA permanece aberto para permitir conversas contínuas
            
            // Outros dropdowns fecham normalmente
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
            if (notificacoesRef.current && !notificacoesRef.current.contains(event.target)) {
                setIsNotificacoesOpen(false);
            }
        };

        if (isMenuOpen || isNotificacoesOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMenuOpen, isNotificacoesOpen]);

    // Função para expandir para página completa
    const handleExpandIA = () => {
        setIsIADropdownOpen(false);
        navigate('/assistente-ia');
    };

    // Obter URL da foto do usuário
    // Verifica se existe e não é string vazia
    const fotoUrlRaw = userFotoUrl || user?.fotoURL;
    const fotoUrl = fotoUrlRaw && fotoUrlRaw.trim() !== '' ? getUsuarioFotoUrl(fotoUrlRaw) : null;

    // Iniciais do nome para avatar padrão
    const getIniciais = () => {
        if (user?.nome || user?.nome_completo) {
            const nome = user.nome || user.nome_completo;
            const partes = nome.trim().split(' ');
            if (partes.length >= 2) {
                return `${partes[0][0]}${partes[partes.length - 1][0]}`.toUpperCase();
            }
            return nome[0].toUpperCase();
        }
        return 'U';
    };

    // Obter apenas o primeiro nome
    const getPrimeiroNome = () => {
        if (user?.nome || user?.nome_completo) {
            const nome = user.nome || user.nome_completo;
            const partes = nome.trim().split(' ');
            return partes[0];
        }
        return 'Usuário';
    };

    const handleMenuClick = (action) => {
        setIsMenuOpen(false);
        
        switch (action) {
            case 'perfil':
                navigate('/profile');
                break;
            case 'permissoes':
                navigate('/config/permissoes');
                break;
            case 'privacidade':
                navigate('/privacidade');
                break;
            case 'assinatura':
                navigate('/config/assinatura/gerenciar');
                break;
            case 'sair':
                handleLogout();
                navigate('/login');
                break;
            default:
                break;
        }
    };

    // 💡 Constante para a cor do ícone
    const iconColorClass = 'text-cor-icone-menu-mobile-quando-selecionado'; // #3b7960

    // Verificar se há usuário (DEPOIS de todos os hooks)
    if (!user) {
        return null;
    }

    return (
        <header className="fixed w-full top-0 z-[45] lg:hidden p-4">
            <div className="w-full mx-auto max-w-lg">
                <div 
                    className="flex justify-between items-center h-14 p-3 px-4 
                                rounded-3xl shadow-lg 
                                bg-white" 
                >
                    {/* 1. Lado Esquerdo: Logo e Nome (Higeia.tech) */}
                    <div className="flex items-center space-x-2">
                        <img 
                            src="/images/higeia-icon-only.png"
                            alt="Higeia.tech Logo"
                            className="h-8 w-8 object-contain"
                        />
                        <span className={`text-xl font-bold ${iconColorClass} tracking-wider`}>
                            Higeia<span className="font-normal text-gray-700">.tech</span>
                        </span>
                    </div>
                    
                    {/* 2. Lado Direito: IA (Premium), Notificações e Menu do Usuário */}
                    <div className="flex items-center space-x-2">
                        {/* Botão de IA (apenas com permissão) */}
                        {hasIAAccess && (
                            <div className="relative" ref={iaDropdownRef}>
                                <button
                                    onClick={() => setIsIADropdownOpen(!isIADropdownOpen)}
                                    className="relative p-1.5 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none border border-purple-200 hover:border-purple-300 bg-purple-50"
                                    aria-label="Assistente de IA"
                                    title="Assistente de IA"
                                >
                                    <Sparkles className="h-4 w-4 text-purple-600" />
                                </button>

                                {/* Dropdown de IA com ChatInterface Mini */}
                                {isIADropdownOpen && (
                                    <div className="fixed sm:absolute right-2 sm:right-0 top-16 sm:top-auto mt-0 sm:mt-2 w-[calc(100vw-3rem)] sm:w-[420px] max-w-[360px] sm:max-w-none z-[60]">
                                        <AIProvider>
                                            <ChatInterfaceMini 
                                                onExpand={handleExpandIA} 
                                                onClose={() => setIsIADropdownOpen(false)}
                                            />
                                        </AIProvider>
                                    </div>
                                )}
                            </div>
                        )}
                        
                        {/* Ícone de Notificações */}
                        <div className="relative" ref={notificacoesRef}>
                            <button
                                onClick={() => setIsNotificacoesOpen(!isNotificacoesOpen)}
                                className="relative p-1.5 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none"
                                aria-label="Notificações"
                            >
                                <Bell className={`h-4 w-4 ${notificacoes.length > 0 ? iconColorClass : 'text-gray-400'}`} />
                                {notificacoes.length > 0 && (
                                    <span className="absolute top-0.5 right-0.5 h-1.5 w-1.5 bg-red-500 rounded-full animate-pulse"></span>
                                )}
                            </button>

                            {/* Dropdown de Notificações */}
                            {isNotificacoesOpen && (
                                <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-[60] max-h-80 overflow-y-auto">
                                    <div className="p-3 border-b border-gray-200">
                                        <h3 className="text-xs font-semibold text-gray-900">Notificações</h3>
                                    </div>
                                    {loadingNotificacoes ? (
                                        <div className="p-3 text-center text-xs text-gray-500">
                                            Carregando...
                                        </div>
                                    ) : notificacoes.length === 0 ? (
                                        <div className="p-3 text-center text-xs text-gray-500">
                                            Nenhuma notificação
                                        </div>
                                    ) : (
                                        <div className="divide-y divide-gray-200">
                                            {notificacoes.map((notificacao) => (
                                                <div
                                                    key={notificacao.id}
                                                    className={`p-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                                                        notificacao.urgente ? 'bg-red-50' : ''
                                                    }`}
                                                    onClick={() => {
                                                        setIsNotificacoesOpen(false);
                                                        if (notificacao.url) {
                                                            navigate(notificacao.url);
                                                        }
                                                    }}
                                                >
                                                    <div className="flex items-start gap-2">
                                                        <div className={`flex-shrink-0 mt-0.5 ${
                                                            notificacao.urgente ? 'text-red-600' : 'text-yellow-600'
                                                        }`}>
                                                            <AlertCircle className="h-4 w-4" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-xs font-semibold text-gray-900">
                                                                {notificacao.titulo}
                                                            </p>
                                                            <p className="text-xs text-gray-600 mt-0.5">
                                                                {notificacao.mensagem}
                                                            </p>
                                                            {notificacao.acao && notificacao.url && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setIsNotificacoesOpen(false);
                                                                        navigate(notificacao.url);
                                                                    }}
                                                                    className="text-xs text-higeia-dark hover:text-higeia-medium mt-1 font-medium"
                                                                >
                                                                    {notificacao.acao} →
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Menu do Usuário */}
                        <div className="relative flex-shrink-0" ref={menuRef}>
                            <button
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                className="flex items-center space-x-1.5 px-2 py-1 rounded-lg hover:bg-gray-50 transition-all duration-200 focus:outline-none"
                                aria-label="Menu do usuário"
                                aria-expanded={isMenuOpen}
                            >
                                {/* Foto do Usuário ou Avatar com Iniciais */}
                                <div className="relative">
                                    {fotoUrl ? (
                                        <img
                                            src={fotoUrl}
                                            alt={`${getPrimeiroNome()}`}
                                            className="h-8 w-8 rounded-full object-cover border-2 border-gray-200"
                                            onError={(e) => {
                                                // Fallback para avatar com iniciais se a imagem falhar
                                                e.target.style.display = 'none';
                                                const fallback = e.target.nextElementSibling;
                                                if (fallback) {
                                                    fallback.style.display = 'flex';
                                                }
                                            }}
                                            onLoad={(e) => {
                                                // Garantir que a imagem está visível quando carregar com sucesso
                                                e.target.style.display = 'block';
                                                const fallback = e.target.nextElementSibling;
                                                if (fallback) {
                                                    fallback.style.display = 'none';
                                                }
                                            }}
                                        />
                                    ) : null}
                                    <div
                                        className={`h-8 w-8 rounded-full bg-higeia-dark flex items-center justify-center text-white font-semibold text-xs ${
                                            fotoUrl ? 'hidden' : 'flex'
                                        }`}
                                    >
                                        {getIniciais()}
                                    </div>
                                </div>

                                {/* Ícone de Chevron */}
                                <ChevronDown 
                                    className={`h-3 w-3 text-gray-500 transition-transform duration-200 ${
                                        isMenuOpen ? 'transform rotate-180' : ''
                                    }`}
                                />
                            </button>

                            {/* Menu Suspenso */}
                            {isMenuOpen && (
                                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[60]">
                                    {/* Meu Perfil */}
                                    <button
                                        onClick={() => handleMenuClick('perfil')}
                                        className="w-full flex items-center space-x-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                                    >
                                        <User className="h-3.5 w-3.5 text-gray-500" />
                                        <span>Meu Perfil</span>
                                    </button>

                                    {/* Permissões */}
                                    <button
                                        onClick={() => handleMenuClick('permissoes')}
                                        className="w-full flex items-center space-x-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                                    >
                                        <Key className="h-3.5 w-3.5 text-gray-500" />
                                        <span>Permissões</span>
                                    </button>

                                    {/* Privacidade */}
                                    <button
                                        onClick={() => handleMenuClick('privacidade')}
                                        className="w-full flex items-center space-x-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                                    >
                                        <Shield className="h-3.5 w-3.5 text-gray-500" />
                                        <span>Privacidade</span>
                                    </button>

                                    {/* Assinatura */}
                                    <button
                                        onClick={() => handleMenuClick('assinatura')}
                                        className="w-full flex items-center space-x-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                                    >
                                        <CreditCard className="h-3.5 w-3.5 text-gray-500" />
                                        <span>Assinatura</span>
                                    </button>

                                    {/* Divisor */}
                                    <div className="border-t border-gray-200 my-1"></div>

                                    {/* Sair */}
                                    <button
                                        onClick={() => handleMenuClick('sair')}
                                        className="w-full flex items-center space-x-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50 transition-colors duration-150"
                                    >
                                        <LogOut className="h-3.5 w-3.5" />
                                        <span>Sair</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default MobileHeader;